import { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSalesforce } from '../hooks/useSalesforce'
import { useTour } from '../context/TourContext'
import { supabase } from '../services/supabaseClient'

// System administrators who have access to /admin
const SYSTEM_ADMINS = [
  'john@flowlogicllc.com'
]

interface NavbarProps {
  onSignOut?: () => Promise<void>;
  isPublic?: boolean;
}

export default function Navbar({ onSignOut, isPublic = false }: NavbarProps) {
  const { user, signOut } = useAuth()
  const { isSuperAdmin, userRole } = useSalesforce({ skipInitialFetch: true })
  const { startPageTour } = useTour()
  const location = useLocation()
  const navigate = useNavigate()
  const [showDropdown, setShowDropdown] = useState(false)
  const [showOrgDropdown, setShowOrgDropdown] = useState(false)
  const [showHelpDropdown, setShowHelpDropdown] = useState(false)
  const [orgs, setOrgs] = useState<any[]>([])
  const [userAccounts, setUserAccounts] = useState<any[]>([])
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(null)
  const [isSystemAdmin, setIsSystemAdmin] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const orgDropdownRef = useRef<HTMLDivElement>(null)
  const helpDropdownRef = useRef<HTMLDivElement>(null)
  
  // Fetch orgs when component mounts or account changes
  useEffect(() => {
    const fetchOrgs = async () => {
      if (!user) return

      try {
        // Get current account ID from localStorage
        const currentAccountId = localStorage.getItem('currentAccountId')

        if (!currentAccountId) {
          console.log('No current account ID found')
          setOrgs([])
          return
        }

        // Check user's role in the current account
        const { data: userAccountRole, error: roleError } = await supabase
          .from('user_account_roles')
          .select(`
            *,
            roles (
              name
            )
          `)
          .eq('user_id', user.id)
          .eq('account_id', currentAccountId)
          .single()

        if (roleError || !userAccountRole) {
          console.error('Error fetching user role:', roleError)
          setOrgs([])
          return
        }

        const roleName = userAccountRole.roles?.name || 'Viewer'
        const isAdminRole = ['Admin', 'Super-Admin'].includes(roleName)

        let orgsToShow = []

        if (isAdminRole) {
          // Admins see all orgs in their current account
          const { data: accountOrgs, error: accountOrgsError } = await supabase
            .from('sf_orgs')
            .select('id, name, is_sandbox, production_org_id')
            .eq('account_id', currentAccountId)
            .order('name')

          if (!accountOrgsError && accountOrgs) {
            orgsToShow = accountOrgs
          }
        } else {
          // Non-admins only see orgs they have explicit access to in the current account
          // First get all orgs in the current account
          const { data: accountOrgs, error: accountOrgsError } = await supabase
            .from('sf_orgs')
            .select('id')
            .eq('account_id', currentAccountId)

          if (!accountOrgsError && accountOrgs && accountOrgs.length > 0) {
            const accountOrgIds = accountOrgs.map(org => org.id)

            // Now check which of these orgs the user has access to
            const { data: userOrgAccess, error: userOrgAccessError } = await supabase
              .from('user_org_access')
              .select('org_id')
              .eq('user_id', user.id)
              .in('org_id', accountOrgIds)

            if (!userOrgAccessError && userOrgAccess && userOrgAccess.length > 0) {
              const accessibleOrgIds = userOrgAccess.map(access => access.org_id)

              // Fetch the full org details for accessible orgs
              const { data: accessibleOrgs, error: accessibleOrgsError } = await supabase
                .from('sf_orgs')
                .select('id, name, is_sandbox, production_org_id')
                .in('id', accessibleOrgIds)
                .order('name')

              if (!accessibleOrgsError && accessibleOrgs) {
                orgsToShow = accessibleOrgs
              }
            }
          }
        }

        // Sort orgs: production first, then sandboxes, all alphabetically
        orgsToShow.sort((a, b) => {
          // Production orgs before sandboxes
          if (a.is_sandbox !== b.is_sandbox) {
            return a.is_sandbox ? 1 : -1
          }
          // Within same type, sort alphabetically
          return a.name.localeCompare(b.name)
        })

        setOrgs(orgsToShow)
      } catch (error) {
        console.error('Error fetching orgs:', error)
        setOrgs([])
      }
    }

    fetchOrgs()
  }, [user, currentAccountId])
  
  // Check if user is a system admin
  useEffect(() => {
    const checkSystemAdmin = async () => {
      if (!user) return
      
      // First check hardcoded list
      if (SYSTEM_ADMINS.includes(user.email || '')) {
        setIsSystemAdmin(true)
        return
      }
      
      // Then check database
      try {
        const { data, error } = await supabase
          .from('system_admins')
          .select('*')
          .eq('email', user.email)
          .maybeSingle()
        
        if (data && !error) {
          setIsSystemAdmin(true)
        }
      } catch (error) {
        // User is not a system admin
        setIsSystemAdmin(false)
      }
    }
    
    checkSystemAdmin()
  }, [user])
  
  // Track current account ID changes
  useEffect(() => {
    const checkAccountId = () => {
      const accountId = localStorage.getItem('currentAccountId')
      setCurrentAccountId(accountId)
    }

    // Listen for storage events (changes from other tabs/windows)
    window.addEventListener('storage', checkAccountId)

    // Also listen for custom event (for same tab changes)
    window.addEventListener('accountChanged', checkAccountId)

    return () => {
      window.removeEventListener('storage', checkAccountId)
      window.removeEventListener('accountChanged', checkAccountId)
    }
  }, [])

  // Fetch user accounts
  useEffect(() => {
    const fetchUserAccounts = async () => {
      if (!user) return

      try {
        const { data: accounts, error } = await supabase
          .from('user_accounts_view')
          .select('*')

        if (error) {
          console.error('Error fetching user accounts:', error)
          return
        }

        const formattedAccounts = accounts?.map(a => ({
          id: a.account_id,
          name: a.account_name || 'Unknown Account',
          role: a.role_name || 'Unknown Role'
        })) || []

        setUserAccounts(formattedAccounts)

        // Set current account from first account or from localStorage
        const savedAccountId = localStorage.getItem('currentAccountId')
        if (savedAccountId && formattedAccounts.some(a => a.id === savedAccountId)) {
          setCurrentAccountId(savedAccountId)
        } else if (formattedAccounts.length > 0) {
          setCurrentAccountId(formattedAccounts[0].id)
          localStorage.setItem('currentAccountId', formattedAccounts[0].id)
        }
      } catch (error) {
        console.error('Error fetching accounts:', error)
      }
    }

    fetchUserAccounts()
  }, [user])
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setShowOrgDropdown(false)
      }
      if (helpDropdownRef.current && !helpDropdownRef.current.contains(event.target as Node)) {
        setShowHelpDropdown(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  // Determine which link is active based on current path
  const isActive = (path: string) => {
    return location.pathname === path;
  }
  
  // Get current orgId from URL
  const getCurrentOrgId = () => {
    const pathParts = location.pathname.split('/')
    
    // Check if first part is a UUID (org ID in new format)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (pathParts[1] && uuidRegex.test(pathParts[1])) {
      return pathParts[1]
    }
    
    // Check legacy URL patterns for orgId
    if (pathParts[1] === 'flows' && pathParts[2]) return pathParts[2]
    if (pathParts[1] === 'entry-criteria' && pathParts[2]) return pathParts[2]
    if (pathParts[1] === 'triggers' && pathParts[2]) return pathParts[2] // Legacy support
    if (pathParts[1] === 'fields' && pathParts[2]) return pathParts[2]
    
    // Check query params as fallback
    const urlParams = new URLSearchParams(location.search)
    return urlParams.get('orgId') || null
  }
  
  // Handle org selection
  const handleOrgSelect = (orgId: string) => {
    setShowOrgDropdown(false)

    // Save this org as the last viewed for the current account
    if (currentAccountId) {
      localStorage.setItem(`lastOrgFor_${currentAccountId}`, orgId)
    }

    // Get current page type from the path
    const pathParts = location.pathname.split('/')
    let pageType = 'dashboard' // default
    let subPath = '' // for flow details, etc.

    // Check if we're on a specific page type
    const currentOrgId = getCurrentOrgId()
    if (currentOrgId && pathParts[1] === currentOrgId) {
      // New URL format: /orgId/pageType/...
      pageType = pathParts[2] || 'dashboard'
      // Capture any sub-paths (e.g., flow/flowId for flow detail pages)
      if (pathParts.length > 3) {
        subPath = '/' + pathParts.slice(3).join('/')
      }
    } else if (pathParts[1] && !currentOrgId) {
      // Old URL format without orgId: /pageType/...
      pageType = pathParts[1]
      if (pathParts.length > 2) {
        subPath = '/' + pathParts.slice(2).join('/')
      }
    }

    // Don't carry over flow IDs or other org-specific sub-paths to new org
    // Just go to the main page type (flows, triggers, etc.)
    const newUrl = `/${orgId}/${pageType}`

    // Force a full page reload to clear any cached state
    window.location.href = newUrl
  }
  
  // Handle account switching
  const handleAccountSwitch = async (accountId: string) => {
    // Save the current org for the current account before switching
    const currentOrgId = getCurrentOrgId()
    if (currentOrgId && currentAccountId) {
      localStorage.setItem(`lastOrgFor_${currentAccountId}`, currentOrgId)
    }

    // Get current page type to maintain it if possible
    const pathParts = location.pathname.split('/')
    let currentPageType = 'dashboard' // default

    // Determine current page type
    if (currentOrgId && pathParts[1] === currentOrgId) {
      currentPageType = pathParts[2] || 'dashboard'
    } else if (pathParts[1] && !currentOrgId) {
      // Could be on /orgs, /settings, etc.
      currentPageType = pathParts[1]
    }

    setCurrentAccountId(accountId)
    localStorage.setItem('currentAccountId', accountId)

    // Fire custom event for same-tab listeners
    window.dispatchEvent(new Event('accountChanged'))

    setShowDropdown(false)

    // Check what org to navigate to in the new account
    try {
      // First, check if there's a last viewed org for this account
      const lastOrgId = localStorage.getItem(`lastOrgFor_${accountId}`)

      // Get the orgs available in the new account
      const { data: userAccountRole } = await supabase
        .from('user_account_roles')
        .select(`
          *,
          roles (
            name
          )
        `)
        .eq('user_id', user.id)
        .eq('account_id', accountId)
        .single()

      const roleName = userAccountRole?.roles?.name || 'Viewer'
      const isAdminRole = ['Admin', 'Super-Admin'].includes(roleName)

      let availableOrgIds = []

      if (isAdminRole) {
        // Admins see all orgs in the account
        const { data: accountOrgs } = await supabase
          .from('sf_orgs')
          .select('id')
          .eq('account_id', accountId)
          .order('name') // Order by name for consistency

        availableOrgIds = accountOrgs?.map(org => org.id) || []
      } else {
        // Non-admins only see orgs they have access to
        const { data: accountOrgs } = await supabase
          .from('sf_orgs')
          .select('id')
          .eq('account_id', accountId)

        if (accountOrgs && accountOrgs.length > 0) {
          const accountOrgIds = accountOrgs.map(org => org.id)

          const { data: userOrgAccess } = await supabase
            .from('user_org_access')
            .select('org_id')
            .eq('user_id', user.id)
            .in('org_id', accountOrgIds)

          availableOrgIds = userOrgAccess?.map(access => access.org_id) || []
        }
      }

      // Determine where to navigate
      let targetUrl = '/orgs' // Default to orgs page if no orgs available

      if (availableOrgIds.length > 0) {
        // We have orgs available
        let targetOrgId = null

        // Check if last viewed org is still available
        if (lastOrgId && availableOrgIds.includes(lastOrgId)) {
          targetOrgId = lastOrgId
        } else {
          // Use first available org
          targetOrgId = availableOrgIds[0]
          // Save this as the last org for next time
          localStorage.setItem(`lastOrgFor_${accountId}`, targetOrgId)
        }

        // Stay on the same page type if it's an org-specific page
        // Otherwise go to dashboard
        const orgSpecificPages = ['dashboard', 'flows', 'entry-criteria', 'triggers', 'settings', 'workspaces']
        if (orgSpecificPages.includes(currentPageType)) {
          targetUrl = `/${targetOrgId}/${currentPageType}`
        } else {
          // For non-org pages (like /orgs, /subscription, etc.), go to dashboard
          targetUrl = `/${targetOrgId}/dashboard`
        }
      }
      // If no orgs available, targetUrl remains '/orgs'

      // Navigate to the target URL
      window.location.href = targetUrl
    } catch (error) {
      console.error('Error switching accounts:', error)
      // Fallback to reload if there's an error
      window.location.reload()
    }
  }
  
  // Get user initials for avatar
  const getUserInitials = (email: string | undefined) => {
    if (!email) return 'U'
    const parts = email.split('@')[0].split('.')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return email[0].toUpperCase()
  }
  
  // Handle sign out
  const handleSignOut = async () => {
    if (onSignOut) {
      await onSignOut()
    } else {
      await signOut()
      navigate('/login')
    }
  }
  
  // Public navbar (for landing page, login, signup)
  if (isPublic) {
    return (
      <header className="bg-white shadow-sm sticky top-0 z-50" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="text-xl font-bold text-gray-800 hover:text-gray-900 flex items-center">
                <img src="/logotype-black.png" alt="FlowDocs Logo" className="h-[2.6rem] mt-1.5" />
              </Link>
            </div>
            <div className="flex items-center justify-center flex-1">
              <nav className="hidden md:flex space-x-12">
                <a href="/#issue-detection" className="text-gray-600 hover:text-gray-900 flex items-center">Issue Detection</a>
                <a href="/#automatic-documentation" className="text-gray-600 hover:text-gray-900 flex items-center">Automatic Documentation</a>
                <a href="/#flow-health" className="text-gray-600 hover:text-gray-900 flex items-center">Flow Health</a>
                <a href="/#field-impact" className="text-gray-600 hover:text-gray-900 flex items-center">Field Impact</a>
                <Link to="/plans" className="text-gray-600 hover:text-gray-900 flex items-center">Pricing</Link>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              {user ? (
                <Link to="/dashboard" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md">
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-blue-600 hover:text-blue-800">Login</Link>
                  <Link to="/signup" className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md">Sign Up</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
    )
  }
  
  // Private navbar (for dashboard and authenticated pages)
  return (
    <nav className="bg-white shadow-sm sticky z-40" style={{ 
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      top: 'var(--banner-height, 0px)'
    }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex-shrink-0 flex items-center">
            <Link 
              to={getCurrentOrgId() ? `/${getCurrentOrgId()}/dashboard` : '/dashboard'} 
              className="text-lg font-bold text-gray-800 hover:text-gray-900 flex items-center"
              onClick={() => window.scrollTo(0, 0)}
            >
              <img src="/logotype-black.png" alt="FlowDocs Logo" className="h-[2.6rem] mt-1.5" />
            </Link>
          </div>
          <div className="flex items-center justify-center flex-1">
            <div className="flex justify-center space-x-8">
              <Link 
                to={getCurrentOrgId() ? `/${getCurrentOrgId()}/dashboard` : '/dashboard'} 
                className={`${location.pathname.includes('/dashboard') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                onClick={() => window.scrollTo(0, 0)}
              >
                Dashboard
              </Link>
              
              {/* Orgs Dropdown */}
              <div 
                className="relative group" 
                ref={orgDropdownRef}
                onMouseEnter={() => setShowOrgDropdown(true)}
                onMouseLeave={() => setShowOrgDropdown(false)}
              >
                <Link
                  to={getCurrentOrgId() ? `/${getCurrentOrgId()}/orgs` : '/orgs'}
                  onClick={(e) => {
                    if (showOrgDropdown) {
                      e.preventDefault()
                      setShowOrgDropdown(false)
                      navigate(getCurrentOrgId() ? `/${getCurrentOrgId()}/orgs` : '/orgs')
                    }
                  }}
                  className={`${location.pathname.includes('/orgs') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                >
                  Orgs
                  <svg className="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
                
                {showOrgDropdown && (
                  <div className="origin-top-left absolute left-0 pt-2 min-w-[14rem] max-w-sm z-50">
                    <div className="rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                      <Link
                        to={getCurrentOrgId() ? `/${getCurrentOrgId()}/orgs` : '/orgs'}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                        role="menuitem"
                        onClick={() => setShowOrgDropdown(false)}
                      >
                        View all Orgs
                      </Link>
                      {orgs.length > 0 && <hr className="my-1" />}
                      {orgs.length > 0 ? (
                        <>
                          {orgs.map((org) => {
                            const isCurrentOrg = org.id === getCurrentOrgId()
                            return (
                              <button
                                key={org.id}
                                onClick={() => handleOrgSelect(org.id)}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                role="menuitem"
                              >
                                {isCurrentOrg ? (
                                  <svg className="mr-2 h-4 w-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <div className="mr-2 h-4 w-4 flex-shrink-0" />
                                )}
                                <div className="flex-1">
                                  <span className={isCurrentOrg ? 'font-semibold' : ''}>
                                    {org.name}
                                  </span>
                                  <span className="text-gray-500 text-xs ml-1">
                                    ({org.is_sandbox ? 'Sandbox' : 'Production'})
                                  </span>
                                </div>
                              </button>
                            )
                          })}
                          {userRole !== 'Viewer' && (
                            <>
                              <hr className="my-1" />
                              <Link
                                to="/connect-salesforce"
                                className="block px-4 py-2 text-sm text-blue-600 hover:bg-gray-100 flex items-center"
                                onClick={() => setShowOrgDropdown(false)}
                              >
                                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Connect an Org
                              </Link>
                            </>
                          )}
                        </>
                      ) : (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          No orgs connected yet
                        </div>
                      )}
                      {orgs.length === 0 && userRole !== 'Viewer' && (
                        <>
                          <hr className="my-1" />
                          <Link
                            to="/connect-salesforce"
                            className="block px-4 py-2 text-sm text-blue-600 hover:bg-gray-100 flex items-center"
                            onClick={() => setShowOrgDropdown(false)}
                          >
                            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Connect an Org
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                  </div>
                )}
              </div>
              
              <Link
                to={getCurrentOrgId() ? `/${getCurrentOrgId()}/flows` : '/flows'}
                className={`${location.pathname.includes('/flows') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Flows
              </Link>
              <Link
                to={getCurrentOrgId() ? `/${getCurrentOrgId()}/fields` : '/fields'}
                className={`${location.pathname.includes('/fields') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Fields
              </Link>
              <Link
                to={getCurrentOrgId() ? `/${getCurrentOrgId()}/entry-criteria` : '/entry-criteria'}
                className={`${(location.pathname.includes('/entry-criteria') || location.pathname.includes('/triggers')) ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Entry Criteria
              </Link>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Help dropdown */}
            <div className="relative" ref={helpDropdownRef}>
              <button
                onClick={() => {
                  setShowHelpDropdown(!showHelpDropdown)
                  setShowDropdown(false)
                  setShowOrgDropdown(false)
                }}
                className="flex items-center text-sm text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showHelpDropdown && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    <Link
                      to="/help/quick-start"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      role="menuitem"
                      onClick={() => setShowHelpDropdown(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                      </svg>
                      Quick Start Guide
                    </Link>
                    <button
                      onClick={() => {
                        setShowHelpDropdown(false)
                        startPageTour()
                      }}
                      className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      role="menuitem"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Tour This Page
                    </button>
                    <Link
                      to="/help"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      role="menuitem"
                      onClick={() => setShowHelpDropdown(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      Documentation
                    </Link>
                    <Link
                      to="/help/contact-support"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      role="menuitem"
                      onClick={() => setShowHelpDropdown(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Get Support
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* User dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md p-1"
              >
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                  {getUserInitials(user?.email)}
                </div>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              {showDropdown && (
                <div className="origin-top-right absolute right-0 mt-2 min-w-[16rem] max-w-sm rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                    {/* User info section */}
                    <div className="px-4 py-3">
                      <div className="text-sm text-gray-900 truncate">
                        {user?.email}
                      </div>
                    </div>
                    
                    {/* Accounts section */}
                    {userAccounts.length > 0 && (
                      <>
                        <div className="border-t border-gray-200"></div>
                        <div className="py-1">
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Accounts
                          </div>
                          {userAccounts.map((account) => (
                            <button
                              key={account.id}
                              onClick={() => handleAccountSwitch(account.id)}
                              className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                                account.id === currentAccountId ? 'bg-gray-50' : ''
                              }`}
                              role="menuitem"
                            >
                              <span className="flex items-center">
                                {account.id === currentAccountId && (
                                  <svg className="mr-2 h-4 w-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                                {account.id !== currentAccountId && (
                                  <div className="mr-2 h-4 w-4 flex-shrink-0" />
                                )}
                                <span className={`${account.id === currentAccountId ? 'font-semibold' : ''} whitespace-nowrap`}>
                                  {account.name}
                                </span>
                              </span>
                            </button>
                          ))}
                        </div>
                        <div className="border-t border-gray-200"></div>
                      </>
                    )}
                    
                    {/* Settings and Sign out section */}
                    <div className="py-1">
                      {(userRole === 'Super-Admin' || userRole === 'Admin') && (
                        <>
                          <Link
                            to="/subscription"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            role="menuitem"
                            onClick={() => setShowDropdown(false)}
                          >
                            <svg className="mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Manage Subscription
                          </Link>
                          <Link
                            to="/plans"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            role="menuitem"
                            onClick={() => setShowDropdown(false)}
                          >
                            <svg className="mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                            </svg>
                            Upgrade Plan
                          </Link>
                        </>
                      )}
                      <Link
                        to={getCurrentOrgId() ? `/${getCurrentOrgId()}/settings` : '/settings'}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        role="menuitem"
                        onClick={() => setShowDropdown(false)}
                      >
                        Account Settings
                      </Link>
                      
                      {/* Admin link for system administrators */}
                      {isSystemAdmin && (
                        <>
                          <div className="border-t border-gray-200"></div>
                          <Link
                            to="/admin/industry-insights"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            role="menuitem"
                            onClick={() => setShowDropdown(false)}
                          >
                            <svg className="mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            Industry Insights
                          </Link>
                          <Link
                            to="/admin"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            role="menuitem"
                            onClick={() => setShowDropdown(false)}
                          >
                            <svg className="mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Admin Home
                          </Link>
                          <Link
                            to="/admin/accounts"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            role="menuitem"
                            onClick={() => setShowDropdown(false)}
                          >
                            <svg className="mr-2 h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            Account Admin
                          </Link>
                          <Link
                            to="/admin/labs"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            role="menuitem"
                            onClick={() => setShowDropdown(false)}
                          >
                            <svg className="mr-2 h-4 w-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                            FlowDocs Labs
                          </Link>
                          <div className="border-t border-gray-200"></div>
                        </>
                      )}
                      
                      <button
                        onClick={() => {
                          setShowDropdown(false)
                          handleSignOut()
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        role="menuitem"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}